import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import api from "../lib/apiClient";
import ProductCard from "../components/ProductCard";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(["All products"]);
  const [category, setCategory] = useState("All products");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("featured");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category !== "All products") params.category = category;
    if (search) params.search = search;
    const t = setTimeout(() => {
      api.get("/products", { params }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(t);
  }, [category, search]);

  const sorted = useMemo(() => {
    const arr = [...products];
    if (sort === "price-asc") arr.sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") arr.sort((a, b) => b.price - a.price);
    else if (sort === "name") arr.sort((a, b) => a.name.localeCompare(b.name));
    return arr;
  }, [products, sort]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="relative overflow-hidden rounded-3xl px-6 sm:px-10 py-12 sm:py-16 border border-[#efe6d2]"
        style={{ backgroundImage: "linear-gradient(90deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.5) 55%, rgba(255,255,255,0.25) 100%), url(/assets/heavenly-cloud-bg.webp)", backgroundSize: "cover", backgroundPosition: "center" }}
        data-testid="shop-banner">
        <span className="eyebrow">Catalog</span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-2 leading-[1.05]">Research Materials for Sale</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Lyophilized research compounds with per-batch Certificates of Analysis, for in-vitro laboratory use only.
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mt-8">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="product-search-input"
            placeholder="Search products"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-11 h-12 bg-white border-border rounded-xl"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground shrink-0">Sort by</span>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="sort-select" className="w-44 h-12 bg-white border-border rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="featured">Featured</SelectItem>
              <SelectItem value="price-asc">Price: Low to High</SelectItem>
              <SelectItem value="price-desc">Price: High to Low</SelectItem>
              <SelectItem value="name">Name A–Z</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex gap-2.5 mt-6 overflow-x-auto no-scrollbar pb-2">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            data-testid={`category-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
            className={`shrink-0 pill px-5 py-2.5 text-sm font-semibold border transition-colors ${
              category === c ? "btn-ink border-transparent" : "bg-white text-foreground/70 border-border hover:text-foreground"
            }`}
          >
            {c === "Bundle & Save" ? <span>• {c}</span> : c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-2xl card-soft overflow-hidden animate-pulse">
              <div className="aspect-square bg-secondary" />
              <div className="p-5 space-y-3">
                <div className="h-5 bg-secondary rounded w-2/3" />
                <div className="h-4 bg-secondary rounded w-1/3" />
              </div>
            </div>
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-24 text-muted-foreground" data-testid="no-products">No products match your search.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8" data-testid="product-grid">
          {sorted.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
